import { useState, useMemo, useRef, useEffect, useCallback, memo } from "react";
import * as Popover from "@radix-ui/react-popover";
import * as Tabs from "@radix-ui/react-tabs";
import { cn } from "@/lib/utils";
import emojiData from "unicode-emoji-json";

// ---------------------------------------------------------------------------
// Data preparation (runs once at module load)
// ---------------------------------------------------------------------------

interface EmojiEntry {
  emoji: string;
  name: string;
  group: string;
}

const GROUP_META: { key: string; icon: string; label: string }[] = [
  { key: "Smileys & Emotion", icon: "😊", label: "Smileys" },
  { key: "People & Body", icon: "🧑", label: "People" },
  { key: "Animals & Nature", icon: "🐾", label: "Animals" },
  { key: "Food & Drink", icon: "🍔", label: "Food" },
  { key: "Travel & Places", icon: "✈️", label: "Travel" },
  { key: "Activities", icon: "⚽", label: "Activities" },
  { key: "Objects", icon: "💡", label: "Objects" },
  { key: "Symbols", icon: "💜", label: "Symbols" },
  { key: "Flags", icon: "🏁", label: "Flags" },
];

// Pre-build grouped arrays and a flat list for search
const GROUPED_EMOJIS: Record<string, EmojiEntry[]> = {};
const ALL_EMOJIS: EmojiEntry[] = [];

for (const [emoji, info] of Object.entries(emojiData)) {
  const entry: EmojiEntry = { emoji, name: info.name, group: info.group };
  ALL_EMOJIS.push(entry);

  if (!GROUPED_EMOJIS[info.group]) GROUPED_EMOJIS[info.group] = [];
  GROUPED_EMOJIS[info.group]!.push(entry);
}

// ---------------------------------------------------------------------------
// Recent emojis (localStorage)
// ---------------------------------------------------------------------------

const RECENT_KEY = "emoji-picker-recent";
const MAX_RECENT = 20;

function getRecent(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (Array.isArray(parsed))
      return parsed
        .filter((e): e is string => typeof e === "string")
        .slice(0, MAX_RECENT);
    return [];
  } catch {
    return [];
  }
}

function pushRecent(emoji: string): string[] {
  const prev = getRecent().filter((e) => e !== emoji);
  const next = [emoji, ...prev].slice(0, MAX_RECENT);
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    // storage full – ignore
  }
  return next;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface IconPickerProps {
  value: string;
  onSelect: (emoji: string) => void;
  className?: string;
}

export const IconPicker = memo(function IconPicker({ value, onSelect, className }: IconPickerProps) {
  // Keep a stable ref for onSelect so the picker doesn't need to re-render
  // when Formik's render-prop creates a new closure on every keystroke
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [recent, setRecent] = useState<string[]>([]);
  const searchRef = useRef<HTMLInputElement>(null);

  // Load recent emojis when popover opens
  useEffect(() => {
    if (open) {
      setRecent(getRecent());
      setTimeout(() => searchRef.current?.focus(), 50);
    } else {
      setSearch("");
    }
  }, [open]);

  // Search filtering – match against the emoji name
  const filteredEmojis = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return null;
    return ALL_EMOJIS.filter((e) => e.name.includes(q)).slice(0, 60);
  }, [search]);

  const handleSelect = useCallback(
    (emoji: string) => {
      const updated = pushRecent(emoji);
      setRecent(updated);
      onSelectRef.current(emoji);
      setSearch("");
      setOpen(false);
    },
    [],
  );

  const renderEmojiButton = useCallback(
    (entry: EmojiEntry | string) => {
      const emoji = typeof entry === "string" ? entry : entry.emoji;
      const name = typeof entry === "string" ? emoji : entry.name;
      return (
        <button
          key={emoji}
          type="button"
          title={name}
          onClick={() => handleSelect(emoji)}
          className={cn(
            "w-9 h-9 md:w-11 md:h-11 flex items-center justify-center rounded-lg text-lg md:text-2xl",
            "transition-all duration-150 cursor-pointer",
            "hover:bg-app-primary/10 hover:scale-110 active:scale-95",
            value === emoji &&
              "bg-app-primary/20 ring-2 ring-app-primary shadow-sm",
          )}
        >
          {emoji}
        </button>
      );
    },
    [value, handleSelect],
  );

  const defaultTab = recent.length > 0 ? "__recent" : GROUP_META[0]!.key;

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          className={cn(
            "w-10 h-10 flex items-center justify-center rounded-xl border border-app-border",
            "bg-app-surface-raised hover:bg-app-surface text-xl",
            "transition-all duration-200 hover:scale-105 active:scale-95",
            "shadow-sm hover:shadow-md cursor-pointer",
            className,
          )}
        >
          {value || "😊"}
        </button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          side="top"
          sideOffset={8}
          align="start"
          className={cn(
            "z-70 w-80 md:w-104 rounded-2xl border border-app-border bg-app-surface p-3 md:p-4 shadow-xl",
            "animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
          )}
        >
          {/* Search input */}
          <div className="relative mb-2">
            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-app-text-muted text-sm pointer-events-none">
              🔍
            </span>
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search emoji..."
              className={cn(
                "w-full pl-8 pr-3 py-1.5 rounded-xl text-sm",
                "bg-app-surface-raised border border-app-border",
                "text-app-text placeholder:text-app-text-muted/50",
                "focus:outline-none focus:ring-2 focus:ring-app-primary/40 focus:border-app-primary",
                "transition-all duration-200",
              )}
            />
          </div>

          {filteredEmojis ? (
            /* Search results */
            <div>
              <p className="text-xs text-app-text-muted mb-1.5 font-medium">
                {filteredEmojis.length
                  ? `Results (${filteredEmojis.length})`
                  : "No results found"}
              </p>
              <div className="grid grid-cols-7 md:grid-cols-8 gap-1 max-h-52 md:max-h-72 overflow-y-auto">
                {filteredEmojis.map(renderEmojiButton)}
              </div>
            </div>
          ) : (
            /* Category tabs (default view) */
            <Tabs.Root defaultValue={defaultTab} key={defaultTab}>
              <Tabs.List className="flex gap-0.5 mb-2 pb-2 border-b border-app-border overflow-x-auto scrollbar-none">
                {recent.length > 0 && (
                  <Tabs.Trigger
                    value="__recent"
                    className={cn(
                      "shrink-0 px-1.5 py-1 rounded-full text-sm md:text-base transition-all duration-200 cursor-pointer",
                      "data-[state=active]:bg-app-primary data-[state=active]:text-white data-[state=active]:shadow-sm",
                      "data-[state=inactive]:bg-app-surface-raised data-[state=inactive]:text-app-text-muted",
                      "hover:scale-105",
                    )}
                    title="Recent"
                  >
                    🕐
                  </Tabs.Trigger>
                )}
                {GROUP_META.map((g) => (
                  <Tabs.Trigger
                    key={g.key}
                    value={g.key}
                    className={cn(
                      "shrink-0 px-1.5 py-1 rounded-full text-sm md:text-base transition-all duration-200 cursor-pointer",
                      "data-[state=active]:bg-app-primary data-[state=active]:text-white data-[state=active]:shadow-sm",
                      "data-[state=inactive]:bg-app-surface-raised data-[state=inactive]:text-app-text-muted",
                      "hover:scale-105",
                    )}
                    title={g.label}
                  >
                    {g.icon}
                  </Tabs.Trigger>
                ))}
              </Tabs.List>

              {/* Recent tab */}
              {recent.length > 0 && (
                <Tabs.Content value="__recent">
                  <p className="text-xs text-app-text-muted mb-1.5 font-medium">
                    Recent
                  </p>
                  <div className="grid grid-cols-7 md:grid-cols-8 gap-1 max-h-52 md:max-h-72 overflow-y-auto">
                    {recent.map((emoji) => renderEmojiButton(emoji))}
                  </div>
                </Tabs.Content>
              )}

              {/* Category tabs */}
              {GROUP_META.map((g) => (
                <Tabs.Content key={g.key} value={g.key}>
                  <p className="text-xs text-app-text-muted mb-1.5 font-medium">
                    {g.label}
                  </p>
                  <div className="grid grid-cols-7 md:grid-cols-8 gap-1 max-h-52 md:max-h-72 overflow-y-auto">
                    {(GROUPED_EMOJIS[g.key] ?? []).map(renderEmojiButton)}
                  </div>
                </Tabs.Content>
              ))}
            </Tabs.Root>
          )}

          <Popover.Arrow className="fill-app-surface" />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}, (prev, next) => prev.value === next.value && prev.className === next.className);
